import { gql, useMutation, useQuery } from "@apollo/client";
import { Close, Edit, Save } from "@mui/icons-material";
import { Snackbar } from "@mui/material";
import {
  DataGridPremium,
  GridActionsCellItem,
  GridColDef,
  GridRenderCellParams,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
} from "@mui/x-data-grid-premium";
import { gridActionSx } from "../../widgets/gridActionSx";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAppSelector } from "../../../app/hooks";
import { isReadonly } from "../../../app/features/auth/authTypes";
import { tokenOptions } from "../../../backend/graphql";
import { Tab } from "./index";

export interface PricingFactor {
  id: string;
  sheetType: string;
  label: string;
  enabled: boolean;
  lowerBound: number;
  upperBound: number;
  factor: number;
}

const PRICING_FACTOR_FRAGMENT = gql`
  fragment PricingFactorFragment on PricingFactor {
    id
    sheetType
    label
    enabled
    lowerBound
    upperBound
    factor
  }
`;

const GET_PRICING_FACTORS = gql`
  query PricingFactors {
    getPricingFactors {
      ...PricingFactorFragment
    }
  }
  ${PRICING_FACTOR_FRAGMENT}
`;

const UPDATE_PRICING_FACTOR = gql`
  mutation UpdatePricingFactor(
    $id: ID!
    $lowerBound: Float!
    $upperBound: Float!
    $enabled: Boolean!
  ) {
    updatePricingFactor(
      id: $id
      lowerBound: $lowerBound
      upperBound: $upperBound
      enabled: $enabled
    ) {
      ...PricingFactorFragment
    }
  }
  ${PRICING_FACTOR_FRAGMENT}
`;

const TABS = {
  sources: { labelFieldHeaderName: "Source", sheetType: "SourceFactor" },
  tags: { labelFieldHeaderName: "Tag", sheetType: "TagFactor" },
} as const;

const Cell = ({
  rowModes,
  params,
}: {
  rowModes: GridRowModesModel;
  params: GridRenderCellParams<PricingFactor>;
}) => {
  const rowMode = rowModes[params.id]?.mode ?? GridRowModes.View;
  if (!params.row.enabled && rowMode === GridRowModes.View) {
    return null;
  }
  return params.value;
};

const getActions = ({
  rowModes,
  setRowModes,
  params,
}: {
  rowModes: GridRowModesModel;
  setRowModes: Dispatch<SetStateAction<GridRowModesModel>>;
  params: GridRowParams<PricingFactor>;
}) => {
  const mode = rowModes[params.id]?.mode ?? GridRowModes.View;
  if (mode === GridRowModes.View) {
    return [
      <GridActionsCellItem
        icon={<Edit />}
        label="Edit"
        sx={gridActionSx}
        onClick={() => {
          setRowModes((prevModel) => ({
            ...prevModel,
            [params.row.id]: { mode: GridRowModes.Edit },
          }));
        }}
      />,
    ];
  } else if (mode === GridRowModes.Edit) {
    return [
      <GridActionsCellItem
        icon={<Close />}
        label="Cancel"
        onClick={() => {
          setRowModes((prevModel) => ({
            ...prevModel,
            [params.row.id]: {
              mode: GridRowModes.View,
              ignoreModifications: true,
            },
          }));
        }}
      />,
      <GridActionsCellItem
        icon={<Save />}
        label="Save"
        sx={gridActionSx}
        onClick={() => {
          setRowModes((prevModel) => ({
            ...prevModel,
            [params.row.id]: {
              mode: GridRowModes.View,
              ignoreModifications: false,
            },
          }));
        }}
      />,
    ];
  }

  return [];
};

const DataGrid = ({ selectedTab }: { selectedTab: Tab }) => {
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const token = useAppSelector((state) => state.auth.token);
  const readonly = isReadonly(useAppSelector((state) => state.auth.role));

  const {
    loading,
    data,
    error: queryError,
  } = useQuery(GET_PRICING_FACTORS, tokenOptions(token));

  const [updatePricingFactor, { error: mutationError }] = useMutation(
    UPDATE_PRICING_FACTOR,
    tokenOptions(token)
  );

  const [showQueryError, setShowQueryError] = useState(false);
  const [showMutationError, setShowMutationError] = useState(false);

  useEffect(() => {
    if (queryError) {
      setShowQueryError(true);
    }
  }, [queryError]);

  useEffect(() => {
    if (mutationError) {
      setShowMutationError(true);
    }
  }, [mutationError]);

  const rows: PricingFactor[] = useMemo(() => {
    if (!data) return [];
    return data.getPricingFactors.filter(
      (factor: any) => factor.sheetType === TABS[selectedTab].sheetType
    );
  }, [data, selectedTab]);

  const processRowUpdate = useCallback(
    async (newRowCandidate: any, oldRow: any) => {
      const fieldsToCheck = ["lowerBound", "upperBound", "enabled"];
      const changed = fieldsToCheck.some(
        (field) => newRowCandidate[field] !== oldRow[field]
      );
      if (!changed) return oldRow;

      try {
        const result = await updatePricingFactor({
          variables: {
            id: newRowCandidate.id,
            lowerBound: newRowCandidate.lowerBound,
            upperBound: newRowCandidate.upperBound,
            enabled: newRowCandidate.enabled,
          },
        });
        return result.data.updatePricingFactor;
      } catch (error) {
        return oldRow;
      }
    },
    [updatePricingFactor]
  );

  const columns = [
    {
      field: "label",
      headerName: TABS[selectedTab].labelFieldHeaderName,
      flex: 2,
      editable: false,
    },
    {
      field: "enabled",
      headerName: "Enabled",
      type: "boolean",
      flex: 1,
      editable: true,
    },
    {
      field: "factor",
      headerName: "Factor",
      type: "number",
      flex: 1,
      editable: false,
      renderCell: (params) => <Cell rowModes={rowModesModel} params={params} />,
    },
    {
      field: "lowerBound",
      headerName: "Factor Lower Bound",
      type: "number",
      flex: 1,
      editable: true,
      renderCell: (params) => <Cell rowModes={rowModesModel} params={params} />,
    },
    {
      field: "upperBound",
      headerName: "Factor Upper Bound",
      type: "number",
      flex: 1,
      editable: true,
      renderCell: (params) => <Cell rowModes={rowModesModel} params={params} />,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 1,
      getActions: (params) =>
        getActions({
          rowModes: rowModesModel,
          setRowModes: setRowModesModel,
          params,
        }),
    },
  ] as GridColDef[];

  // A read-only user sees the factors, with no edit buttons and no cell it could edit.
  const shownColumns = readonly
    ? columns
        .filter((column) => column.field !== "actions")
        .map((column) => ({ ...column, editable: false }))
    : columns;

  return (
    <>
      <DataGridPremium
        autoHeight
        loading={loading}
        columns={shownColumns}
        rows={rows}
        getRowId={(row) => row.id}
        initialState={{
          sorting: {
            sortModel: [
              { field: "enabled", sort: "desc" },
              { field: "label", sort: "asc" },
            ],
          },
        }}
        editMode="row"
        rowModesModel={rowModesModel}
        processRowUpdate={processRowUpdate}
      />
      <Snackbar
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={showQueryError}
        onClose={() => setShowQueryError(false)}
        message={queryError?.message}
        key={"queryError"}
      />
      <Snackbar
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={showMutationError}
        onClose={() => setShowMutationError(false)}
        message={mutationError?.message}
        key={"mutationError"}
      />
    </>
  );
};

export default DataGrid;
